import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Form } from "antd";
import { Id, Row } from "@silevis/reactgrid";
import {
  getColumns,
  getDataTextByAppPart,
  getMaxHeightByAppPart,
  getStyle,
} from "@lhd/components/CloneApp/pages/SheetApp/untils";
import { CellCustomType } from "@lhd/components/CloneApp/pages/SheetApp/type";
import { TYPE_CELLS } from "@lhd/components/CloneApp/pages/SheetApp/contants";
import { IPermissionItem, TableData } from "@lhd/types";
import { AppSelectionList } from "types/app.type";
import { getTypeByAppPart } from "@lhd/components/CloneApp/pages/SheetApp/untils";
import { MIN_ROW_HEIGHT, calcRowHeight } from "../utils";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { APP_PART_TYPES } from "@lhd/features/Apps/AppForm/constants";

interface UseTableRowsProps {
  dataColumns: ReturnType<typeof getColumns>;
  columns: any;
  containerRef: React.RefObject<HTMLDivElement>;
  tableData?: TableData;
  selectionDataList: AppSelectionList;
  form: ReturnType<typeof Form.useFormInstance>;
  tablePathValue: (string | number)[];
  tablePath: (string | number)[];
  permission?: IPermissionItem;
  isDisabled?: boolean;
  onChange?: () => void;
  getManualHeight?: (clientId?: string) => number | undefined;
}

export const renumberRows = (
  rows: Row<CellCustomType>[],
): Row<CellCustomType>[] =>
  rows.map((row, i) => {
    if (row.rowId === "header") return row;
    return {
      ...row,
      cells: row.cells.map((c, ci) => ({
        ...c,
        rowIndex: i,
        ...(ci === 0 ? { text: String(i) } : {}),
      })),
    };
  });

const getTypeByAppPartLocal = (appPart: AppPartType): string => {
  if (appPart.app_part_type === APP_PART_TYPES.TEXT_AREA)
    return TYPE_CELLS.TEXT_AREA_CELL;
  return getTypeByAppPart(appPart);
};

const getCellSubmitValue = (cell: CellCustomType) => {
  const c = cell as CellCustomType & {
    valueSubmitAttachment?: unknown;
    valueSubmit?: unknown;
  };
  return c.valueSubmitAttachment ?? c.valueSubmit ?? c.text ?? "";
};

const buildRowFormValue = (
  clientId: string,
  rowOrder: number,
  cells: {
    app_part_id: number | string;
    value?: unknown;
    text_color?: string | null;
    background_color?: string | null;
  }[],
) => {
  const rowValue: Record<string, any> = {};
  cells.forEach((cell) => {
    rowValue[String(cell.app_part_id)] = {
      value: cell.value ?? "",
      text_color: cell.text_color ?? undefined,
      background_color: cell.background_color ?? undefined,
    };
  });
  return {
    client_id: clientId,
    row_order: rowOrder,
    value: rowValue,
  };
};

/** Form shape: tables[tableId][clientId] = { client_id, row_order, value: { [fieldId]: { value } } } */
const buildFormValueFromTableData = (data?: TableData) => {
  const tableValue: Record<string, any> = {};
  (data?.rows || []).forEach((row, index) => {
    const clientId = String(row.client_id);
    tableValue[clientId] = buildRowFormValue(
      clientId,
      row.row_order ?? index,
      row.cells,
    );
  });
  return tableValue;
};

const buildFormValueFromRows = (rows: Row<CellCustomType>[]) => {
  const tableValue: Record<string, any> = {};
  rows
    .filter((r) => r.rowId !== "header")
    .forEach((row, index) => {
      const clientId = String(row.rowId);
      const cells = row.cells
        .filter((cell) => (cell as CellCustomType).appPart?.id)
        .map((cell) => ({
          app_part_id: String((cell as CellCustomType).appPart!.id),
          value: getCellSubmitValue(cell as CellCustomType),
          text_color: (cell as CellCustomType).text_color,
          background_color: (cell as CellCustomType).background_color,
        }));
      tableValue[clientId] = buildRowFormValue(clientId, index, cells);
    });
  return tableValue;
};

export const useTableRows = ({
  dataColumns,
  columns,
  containerRef,
  tableData,
  selectionDataList,
  form,
  tablePathValue,
  tablePath,
  permission,
  isDisabled,
  onChange,
  getManualHeight,
}: UseTableRowsProps) => {
  const buildCells = (
    row: TableData["rows"][number],
    rowIndex: number,
  ): CellCustomType[] => {
    const isOdd = rowIndex % 2 === 0;

    const indexCell: CellCustomType = {
      type: "text" as const,
      text: String(rowIndex),
      rowIndex,
      cellIndex: 0,
      nonEditable: true,
      style: {
        background: "#fff",
        color: "#999",
        justifyContent: "center",
      },
    } as any;

    const dataCells = dataColumns.map((col, i) => {
      const cellIndex = i + 1;
      const cell = row.cells.find(
        (c) => String(c.app_part_id) === col.columnId,
      );
      const cellData = getDataTextByAppPart(
        col.appPart,
        cell?.value || "",
        selectionDataList,
      );
      const type = getTypeByAppPartLocal(col.appPart);
      return {
        type,
        rowIndex,
        cellIndex,
        clientId: row.client_id,
        id: cell?.id,
        ...cellData,
        text_color: cell?.text_color ?? undefined,
        background_color: cell?.background_color ?? undefined,
        style: getStyle(false, {
          color: cell?.text_color ?? undefined,
          background: cell?.background_color ?? undefined,
        }),
        selectionDataList,
        columnId: col.columnId as string,
        appPart: col.appPart,
        onChange: () => {
          onChange?.();
        },
        maxHeight: getMaxHeightByAppPart(col.appPart),
        nonEditable: isDisabled || (!permission?.edit && !permission?.delete), // editable when user has permission to edit or delete and not disabled => nonEditable = false else true
      };
    });

    return [indexCell, ...(dataCells as any)];
  };

  const buildEmptyRow = (rowIndex: number): Row<CellCustomType> => {
    const clientId = uuidv4();
    const indexCell: CellCustomType = {
      type: "text" as const,
      text: String(rowIndex),
      rowIndex,
      cellIndex: 0,
      nonEditable: true,
      style: {
        background: "#fff",
        color: "#999",
        justifyContent: "center",
      },
    } as any;
    const dataCells = dataColumns.map((col, i) => ({
      type: getTypeByAppPart(col.appPart),
      rowIndex,
      cellIndex: i + 1,
      status: "new" as const,
      style: getStyle(false),
      selectionDataList,
      columnId: col.columnId as string,
      appPart: col.appPart,
      nonEditable: isDisabled || (!permission?.edit && !permission?.delete), // editable when user has permission to edit or delete and not disabled => nonEditable = false else true
      clientId: clientId,
      maxHeight: getMaxHeightByAppPart(col.appPart),
      onChange: () => {
        onChange?.();
      },
      ...getDataTextByAppPart(col.appPart, "", selectionDataList),
    }));
    return {
      rowId: clientId,
      height: MIN_ROW_HEIGHT,
      cells: [indexCell, ...dataCells],
      reorderable: true,
    } as Row<CellCustomType>;
  };

  const buildRows = (): Row<CellCustomType>[] => {
    const headerCells: CellCustomType[] = [
      {
        type: TYPE_CELLS.TABLE_TOGGLE_CELL as any,
        text: "#",
        rowIndex: 0,
        cellIndex: 0,
        nonEditable: true,
        style: { fontWeight: "bold", justifyContent: "center" },
      } as any,
      ...dataColumns.map((col, i) => ({
        type: "text" as const,
        text: col.label || "",
        rowIndex: 0,
        cellIndex: i + 1,
        nonEditable: true,
        canReorderRows: true,
        appPart: col.appPart,
        columnId: col.columnId as string,
      })),
    ];

    let dataRows: Row<CellCustomType>[] = (tableData?.rows || []).map(
      (row, index) => {
        const rowIndex = index + 1;
        const cells = buildCells(row, rowIndex);
        const rowId = String(row.client_id);

        const initH = Math.max(
          MIN_ROW_HEIGHT,
          calcRowHeight(cells, containerRef.current),
          getManualHeight?.(rowId) ?? 0,
        );

        const cellsWithHeight = buildCells(row, rowIndex).map((c) => ({
          ...c,
          rowHeight: initH,
        }));

        return {
          rowId,
          reorderable: true,
          height: initH,
          cells: cellsWithHeight,
          id: row.row_id,
        } as Row<CellCustomType> & { id: number };
      },
    );

    if (dataRows.length === 0) {
      dataRows = [buildEmptyRow(1)];
    }

    return [{ rowId: "header", height: 45, cells: headerCells }, ...dataRows];
  };

  const [rows, setRows] = useState<Row<CellCustomType>[]>(buildRows);

  const syncRowsToForm = (
    nextRows: Row<CellCustomType>[],
    sourceTableData?: TableData,
  ) => {
    const formValue =
      sourceTableData?.rows?.length && sourceTableData.rows.length > 0
        ? buildFormValueFromTableData(sourceTableData)
        : buildFormValueFromRows(nextRows);
    const existingDestroy = form.getFieldValue([...tablePathValue, "_destroy"]);
    if (existingDestroy) {
      formValue._destroy = existingDestroy;
    }
    form.setFieldValue(tablePathValue, formValue);
  };

  useEffect(() => {
    const nextRows = buildRows();
    setRows(nextRows);
    syncRowsToForm(nextRows, tableData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, columns, isDisabled, permission?.edit, permission?.delete]);

  const handleAddRow = (
    position: "before" | "after" = "after",
    countNumber?: number,
    insertAtRowId?: Id,
  ) => {
    const count = countNumber || 1;
    setRows((prev) => {
      const newRows: Row<CellCustomType>[] = [];
      for (let i = 0; i < count; i++) {
        newRows.push(buildEmptyRow(prev.length + i));
      }

      let next: Row<CellCustomType>[];
      if (!insertAtRowId) {
        next = [...prev, ...newRows];
      } else {
        const idx = prev.findIndex((r) => r.rowId === insertAtRowId);
        next = [...prev];
        const insertIndex =
          idx === -1 ? next.length : position === "before" ? idx : idx + 1;
        next.splice(insertIndex, 0, ...newRows);
      }

      next = renumberRows(next);

      const tableValue = { ...(form.getFieldValue(tablePathValue) || {}) };
      next
        .filter((r) => r.rowId !== "header")
        .forEach((r, index) => {
          const id = String(r.rowId);
          tableValue[id] = {
            ...tableValue[id],
            row_order: index,
            client_id: id,
          };
        });
      form.setFieldValue(tablePathValue, tableValue);

      return next;
    });
    onChange?.();
  };

  const syncFormAndRows = (
    nextRows: Row<CellCustomType>[],
    deletedIds: string[],
  ) => {
    const tableValue = { ...(form.getFieldValue(tablePathValue) || {}) };
    deletedIds.forEach((id) => delete tableValue[id]);
    nextRows
      .filter((r) => r.rowId !== "header")
      .forEach((r, index) => {
        const id = String(r.rowId);
        tableValue[id] = { ...tableValue[id], row_order: index, client_id: id };
      });
    form.setFieldValue(tablePathValue, tableValue);
  };

  const handleDeleteRows = (rowIds: Id[]) => {
    if (permission?.delete === false) return;
    const clientIdStrs = rowIds.map(String);

    const rowsToDelete = rows.filter((r) =>
      clientIdStrs.includes(String(r.rowId)),
    ) as (Row<CellCustomType> & { id?: number })[];

    const oldRows = rowsToDelete.filter((r) => r.id);
    if (oldRows.length > 0) {
      const dataPre = form.getFieldValue([...tablePath, "_destroy"]) || [];
      form.setFieldValue(
        [...tablePath, "_destroy"],
        [...dataPre, ...oldRows.map((r) => ({ row_id: r.id, _destroy: true }))],
      );
    }

    let nextRows = rows.filter((r) => !clientIdStrs.includes(String(r.rowId)));

    if (nextRows.length <= 1) {
      nextRows = [...nextRows, buildEmptyRow(1)];
    }

    nextRows = renumberRows(nextRows);
    syncFormAndRows(nextRows, clientIdStrs);
    setRows(nextRows);
    onChange?.();
  };

  const handleDeleteRow = (rowId: Id) => handleDeleteRows([rowId]);

  return { rows, setRows, handleAddRow, handleDeleteRow, handleDeleteRows };
};
